import * as log from 'loglevel';
import RenderUtil from './renderer/render-util';

// Line-item links of the export extension, one per preview export entry ( main view and each gallery view ).
// classic:   <convert>/export/<identifier>/<id>.<format>?c=<Base64 render config>
// transform: <convert>/transform/export/<identifier>/<id>.<ext>?e=<hash>&c=<Base64 render config>
// An entry the backend published as a pipeline config is served with its `hash` + `ext` and gets the transform link:
// the convert service renders the design SVG ( `<identifier>/<id>.svg` ) at the entry's size and runs the published
// pipeline over it, whatever the output ( `ext` ).

// the published encode formats
const OUT_FORMATS = ['jpg', 'png', 'tiff', 'pdf'];
// the order archive stores the verbatim link in a VARCHAR(255)
const MAX_STORED_URL = 255;

// the convert service of the api environment ( local renders on dev )
export function convertBase(api) {
    return api.replace('api', 'convert').replace('local', 'dev');
}

// the renderer's export config ( SvgExportService.Config ) as Base64 JSON - minted the same way for both link forms
// area: the print area of an entry with `area` ( printArea ) - its print size replaces the entry's size, its box the crop
export function exportRenderToken(config, area = null) {
    if (area) {
        return btoa(JSON.stringify({ w: area.width, h: area.height, u: area.unit, dpi: config.dpi, cW: area.crop.width, cH: area.crop.height, cX: area.crop.x, cY: area.crop.y }));
    }

    let c = { w: config.width, h: config.height, u: config.unit, dpi: config.dpi };

    // optional crop
    if (config.crop && config.cropConfig) {
        c.cW = config.cropConfig.width;
        c.cH = config.cropConfig.height;
        c.cX = config.cropConfig.x;
        c.cY = config.cropConfig.y;
    }

    return btoa(JSON.stringify(c));
}

// the print area of an entry with `area`: the area layer of that key visible for the order ( areas: the preview's last
// render registry, { key: layer } ) with its print size ( physicalWidth / physicalHeight / physicalUnit ) and its box as
// the crop - null when no area of the key is visible, or it has no print size ( the entry exports nothing )
export function printArea(config, areas) {
    const layer = areas && areas[config.area];
    // NEW: an area linked to a dimension option: the entered size and the box fitted to it ( RenderUtil.linkedAreaParams )
    const params = layer && RenderUtil.areaParams(layer);
    if (!params) {
        return null;
    }
    const width = Number(params.physicalWidth);
    const height = Number(params.physicalHeight);
    if (!(width > 0) || !(height > 0) || !params.physicalUnit) {
        return null;
    }
    // the box as rendered ( registered with the preview's geometry defaults, see product-preview _renderLayer )
    const box = layer._box || params;
    return {
        width, height, unit: params.physicalUnit,
        crop: { x: Math.round(box.x || 0), y: Math.round(box.y || 0), width: Math.round(box.width), height: Math.round(box.height) }
    };
}

// whether the entry gets the transform link: a published hash + a known output - anything else keeps the classic link
export function isTransformExport(config) {
    return !!config && typeof config.hash === 'string' && !!config.hash && OUT_FORMATS.includes(config.ext);
}

// the link of one export entry for the uploaded design SVG ( file path `<identifier>/<id>.svg` )
// areas: the preview's visible area layers ( { key: layer } ), for an entry with a print area - null when that entry has
// no visible area with a print size ( debug logged: the order line gets no link for it, a conditional export )
export function exportConfigUrl(api, svgPath, config, embed = false, areas = null) {

    let area = null;
    if (config.area) {
        area = printArea(config, areas);
        if (!area) {
            log.debug('Export print area not visible or without a print size; the export is skipped (a conditional export)', config.property, config.area);
            return null;
        }
    }

    let c = exportRenderToken(config, area);

    if (isTransformExport(config)) {
        // the convert service strips the extension and reads the design SVG; c is url-encoded ( Base64 + and / )
        let url = convertBase(api) + '/transform/export/' + svgPath.replace(/\.svg$/, '') + '.' + config.ext + '?e=' + config.hash + '&c=' + encodeURIComponent(c);

        if (url.length > MAX_STORED_URL) {
            log.warn('Export url exceeds 255 characters; the order archive may reject it', config.property, url.length);
        }

        return url;
    }

    let path = svgPath.replace('.svg', '.' + config.format);
    let url = convertBase(api) + '/export/' + path + '?c=' + c;

    // SVG export with embedded images/fonts
    if (embed && config.format === 'svg') {
        url += '&embed';
    }

    return url;
}

// an upload layer's full-resolution photo for the convert service's svg render: the upload field's first jpg/png export
// of the rendered file ( fileIndex, one based like upload-lift's `<property>_<n>` inputs ) as data-cl-export on the
// <image> of `src` ( Java renders the href, the convert service swaps it in when the export keeps the image's shape ) -
// rotate is the clockwise rotation baked into the preview image
export function withUploadExport(svg, src, field, rotate, fileIndex = 1) {
    const entry = field?.config?.exports?.find(e => e?.property && e.hash && (e.ext === 'jpg' || e.ext === 'png'));
    // a single upload writes `<property>`, a multiple one `<property>_<n>`
    const selector = `input[name$="[${entry?.property}_${fileIndex}]"]` + (fileIndex === 1 ? `, input[name$="[${entry?.property}]"]` : '');
    const input = entry && field.element?.querySelector(selector);
    if (!input?.value) {
        return svg;
    }

    const href = 'xlink:href="' + src + '"';
    let attrs = ' data-cl-export="' + input.value.replace(/&/g, '&amp;') + '"';
    if (rotate) {
        attrs += ' data-cl-export-rot="' + rotate + '"';
    }
    // every image of the same source not yet decorated ( two layers of one source: a duplicate attribute breaks the svg )
    return svg.split(href).map((part, i) => i && !part.startsWith(' data-cl-export=') ? attrs + part : part).join(href);
}

// The svg id of a layer's k-th canvas object ( k from 1 ): `cl-<name>`, then `cl-<name>.2`, ... - every
// UTF-8 byte of the name outside [A-Za-z0-9-] written as `_` + two hex digits. The convert service finds a layer's
// objects by this id ( a pipeline's cut line along a layer: web-convert-bun/src/design-svg.ts layerSvgId ).
export function layerSvgId(name, k) {
    let enc = '';
    for (const b of new TextEncoder().encode(String(name))) {
        const c = String.fromCharCode(b);
        enc += /[A-Za-z0-9-]/.test(c) ? c : '_' + b.toString(16).padStart(2, '0');
    }
    return 'cl-' + enc + (k > 1 ? '.' + k : '');
}

const svgNum = (v) => parseFloat(Number(v).toFixed(3));
const escapeXml = (s) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&apos;');

// The svg of a text on a path ( text-renderer.js textPath ), as the canvas draws it: fabric 4 ( and 6 ) writes its
// characters on one straight line. The canvas draws each grapheme at its own point and angle on the path ( fabric
// _setGraphemeOnPath: renderLeft, renderTop, angle in the object's centre frame, baseline on the path ), so each
// becomes its own <text> with that transform; the <tspan rotate> attribute would be shorter, but librsvg ignores it.
// The canvas paints every glyph's stroke, then every fill ( paintFirst ), so a text with both is written in two
// passes and a glyph's stroke never covers its neighbour's fill. Null when the path layout is missing.
function pathTextSvg(text) {
    if (!text._textLines || !text.__charBounds) {
        return null;
    }
    // fabric measures lazily ( a path text takes its size from the path, so nothing may have measured it yet )
    text._textLines.forEach((line, l) => text.__charBounds[l] || text.measureLine(l));
    // a line's bounds end with a cursor box that has no place on the path: only the graphemes' boxes count
    const bounds = text.__charBounds;
    if (text._textLines.some((line, l) => line.some((g, i) => bounds[l]?.[i]?.renderLeft === undefined))) {
        return null;
    }
    const glyphs = [];
    text._textLines.forEach((line, l) => line.forEach((grapheme, i) => {
        if (!grapheme.trim()) {
            return;
        }
        const box = bounds[l][i];
        const style = text._getStyleDeclaration(l, i) || {};
        const spanStyle = text.getSvgSpanStyles(style, false);
        glyphs.push({
            transform: 'translate(' + svgNum(box.renderLeft) + ' ' + svgNum(box.renderTop) + ') rotate(' + svgNum(box.angle * 180 / Math.PI) + ')',
            span: '<tspan x="' + svgNum(-box.width / 2) + '" y="' + svgNum(style.deltaY || 0) + '"' + (spanStyle ? ' style="' + spanStyle + '"' : '') + '>'
                + escapeXml(grapheme) + '</tspan>'
        });
    }));
    const pass = (overrides) => {
        const saved = Object.keys(overrides).map(key => [key, text[key]]);
        Object.assign(text, overrides);
        try {
            // fabric's own <text> open tag ( font, style, paint order ) for this pass's fill / stroke
            const open = text._wrapSVGTextAndBg({ textBgRects: [], textSpans: [] }).join('').replace(/<\/text>\s*$/, '');
            return glyphs.map(g => open.replace('<text ', '<text transform="' + g.transform + '" ') + g.span + '</text>\n').join('');
        } finally {
            saved.forEach(([key, value]) => { text[key] = value; });
        }
    };
    const bg = [];
    text._setSVGBg(bg);
    const both = text.fill && text.stroke && text.strokeWidth;
    const passes = !both ? [{}] : text.paintFirst === 'stroke' ? [{ fill: null }, { stroke: null }] : [{ stroke: null }, { fill: null }];
    return [bg.join('')].concat(passes.map(pass));
}

// The design svg of the canvas, written once with the export's edits on the objects, then the objects restored:
// - layer ids: every layer object carries its layer id ( fabric writes `id` on the object's group ). A mask layer has no
//   canvas object: its clip paths ( the canvas mask, or each masked object's ) carry the mask layer's ids instead, so a
//   cut line can follow the mask shape ( mask-renderer.js _maskLayer ).
// - area clips: an absolute mask nests the layer's area clip in the mask clip ( mask-renderer.js render() ), and fabric 4
//   writes no nested clip path ( `clip-path="url(#undefined)"` on the mask shape ). Both clips are in canvas
//   coordinates: the nested clip is taken off the mask and the object's markup is wrapped in a group clipped by the
//   area, which intersects the two.
// - curved text: every text on a path is written as the canvas draws it ( pathTextSvg ).
export function designSvg(canvas, svgOptions) {
    const undo = [];
    // set a property until the svg is written ( an inherited one is deleted again, an own one put back )
    const set = (obj, key, value) => {
        const own = Object.prototype.hasOwnProperty.call(obj, key);
        const old = obj[key];
        undo.push(() => {
            if (own) {
                obj[key] = old;
            } else {
                delete obj[key];
            }
        });
        obj[key] = value;
    };

    const counts = new Map();
    const tag = (obj, name) => {
        const k = (counts.get(name) || 0) + 1;
        counts.set(name, k);
        set(obj, 'id', layerSvgId(name, k));
    };
    const clips = new Set();
    const tagClip = (clip) => {
        if (!clip?._maskLayer || clips.has(clip)) {
            return;
        }
        clips.add(clip);
        // fabric 4 writes no id on a group's clip path markup ( an svg file mask ): its first shape carries it
        let shape = clip;
        while (shape._objects?.length) {
            shape = shape._objects[0];
        }
        tag(shape, clip._maskLayer);
    };

    let areas = 0;
    tagClip(canvas.clipPath);
    canvas.forEachObject((obj) => {
        if (obj._layer?.name) {
            tag(obj, obj._layer.name);
        }
        tagClip(obj.clipPath);

        const mask = obj.clipPath;
        const area = mask?._mask ? mask.clipPath : null;
        if (area && mask.absolutePositioned && area.absolutePositioned) {
            const toSVG = obj.toSVG;
            const id = 'CLIPPATH_AREA_' + (++areas);
            set(mask, 'clipPath', undefined);
            set(obj, 'toSVG', function (reviver) {
                return '<g clip-path="url(#' + id + ')" >\n<clipPath id="' + id + '" >\n' + area.toClipPathSVG(reviver) + '</clipPath>\n'
                    + toSVG.call(this, reviver) + '</g>\n';
            });
        }

        if (obj.path && typeof obj._wrapSVGTextAndBg === 'function') {
            const _toSVG = obj._toSVG;
            set(obj, '_toSVG', function () {
                // the curve relies on fabric internals: a failure writes the flat text, never blocks the add to cart
                try {
                    return pathTextSvg(this) || _toSVG.call(this);
                } catch (e) {
                    log.warn('Curved text svg failed, writing flat text', e);
                    return _toSVG.call(this);
                }
            });
        }
    });

    try {
        return canvas.toSVG(svgOptions);
    } finally {
        undo.reverse().forEach(restore => restore());
    }
}
